/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectLegalStandard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import SelectLegalRequirementCard from "./SelectLegalRequirementCard";
import { HandleApiActions, legalRequirements } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";

function SelectLegalStandard({
  componentType,
  setComponentType,
  setSelectedLegal,
  displayPageMap,
  displayPage,
  setDisplayPage,
  selectedLegal,
  savedLinkages,
  disableLrRadioButton,
  disabledLegalRequirement,
  shouldShowLegalRequirementLinkedInfo = () => false,
  selectedStandard,
  setSelectedStandard,
  selectedChapter,
  setSelectedChapter,
  aiRecommendedEntities = [],
}) {
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails
  );

  const dispatch = useDispatch();
  const [legalStandardList, setLegalStandardList] = useState([]);
  const [legalStandardPage, setLegalStandardPage] = useState(1);
  const [legalStandardRowsPerPage, setLegalStandardRowsPerPage] = useState(25);
  const [legalChapterPage, setLegalChapterPage] = useState(1);
  const [legalChapterRowsPerPage, setLegalChapterRowsPerPage] = useState(25);
  const [selectedLegalStandard, setSelectedLegalStandard] = useState(null);
  const [legalStandardCount, setLegalStandardCount] = useState(0);
  const [legalChapterList, setLegalChapterList] = useState([]);
  const [selectedLegalChapter, setSelectedLegalChapter] = useState(null);
  const [legalChapterCount, setLegalChapterCount] = useState(0);
  const [legalRequirementPage, setLegalRequirementPage] = useState(1);
  const [legalRequirementRowsPerPage, setLegalRequirementRowsPerPage] =
    useState(25);
  const [legalRequirementList, setLegalRequirementList] = useState([]);

  const [legalRequirementCount, setLegalRequirementCount] = useState(0);

  const getChaptersForLs = async (item) => {
    const modifiedLegalChapters = item.chapters.map((chapter) => {
      return {
        chapter: chapter,
      };
    });
    setLegalChapterList(modifiedLegalChapters);
    setDisplayPage(displayPageMap.lc);
  };

  // ADD ONLY — build O(1) lookup for AI suggested legal requirements
  const aiSuggestedLegalIds = useMemo(() => {
    return new Set(
      aiRecommendedEntities
        .filter((e) => e.entityType === "LEGAL_REQUIREMENT")
        .map((e) => Number(e.entityId))
    );
  }, [aiRecommendedEntities]);

  return (
    <SelectLegalRequirementCard
      savedLinkages={savedLinkages}
      disableLrRadioButton={disableLrRadioButton}
      displayPage={displayPage}
      displayPageMap={displayPageMap}
      componentType={componentType}
      setComponentType={setComponentType}
      lsData={legalStandardList}
      lcData={legalChapterList}
      // selectedItem={null}
      selectedLegalStandard={selectedLegalStandard}
      getChangedLsItem={(item) => {
        setSelectedStandard(item.standard_number);
        setSelectedLegalStandard(item);
        getChaptersForLs(item);
      }}
      lsApiAction={legalRequirements.get_legal_standard_details_new}
      searchKey={"standard_number"}
      placeholder={"Select Legal Standard"}
      getDataOnSearch={(data, resp) => {
        setLegalStandardList(data || []);
        setLegalStandardCount(resp?.total_count);
      }}
      lcParams={{
        ...(selectedProjectDetails?.delivery_market &&
          selectedProjectDetails?.delivery_market.length && {
          markets: selectedProjectDetails?.delivery_market?.join("_"),
        }),
        page_number: legalChapterPage,
        page_size: legalChapterRowsPerPage,
        module_type: componentType,
      }}
      lsParams={{
        ...(selectedProjectDetails?.delivery_market &&
          selectedProjectDetails?.delivery_market.length && {
          markets: selectedProjectDetails?.delivery_market,
        }),
        page_number: legalStandardPage,
        page_size: legalStandardRowsPerPage,
        ...(componentType !== "all" && {
          module_type: componentType,
          effective_from: selectedVariant?.production_year,
        }),
      }}
      selectedLegalChapter={selectedLegalChapter}
      getChangedLcItem={(item) => {
        setSelectedChapter(item.chapter);
        setSelectedLegalChapter(item.chapter);
        setDisplayPage(displayPageMap.lr);
      }}
      legalStandardRowsPerPage={legalStandardRowsPerPage}
      legalStandardPage={legalStandardPage}
      setLegalStandardRowsPerPage={setLegalStandardRowsPerPage}
      setLegalStandardPage={setLegalStandardPage}
      legalChapterCount={legalChapterCount}
      setLegalChapterCount={setLegalChapterCount}
      legalChapterPage={legalChapterPage}
      setLegalChapterPage={setLegalChapterPage}
      legalChapterRowsPerPage={legalChapterRowsPerPage}
      legalStandardCount={legalStandardCount}
      legalRequirementsParams={{
        ls_id: selectedLegalStandard?.ls_id,
        chapter: selectedLegalChapter,
        page_number: legalRequirementPage,
        page_size: legalRequirementRowsPerPage,
      }}
      legalRequirementApi={legalRequirements.get_legal_requirements_details_new}
      getLrDataOnSearch={(data, resp) => {
        setLegalRequirementList(data);
        setLegalRequirementCount(resp?.total_count);
      }}
      lrSearchKey={"req_number"}
      lrPlaceholder={"Select Legal Requirement"}
      lrPage={legalRequirementPage}
      lrRowsPerPage={legalRequirementRowsPerPage}
      setLrPage={setLegalRequirementPage}
      totalLrCount={legalRequirementCount}
      legalRequirementList={legalRequirementList}
      disabledLegalRequirement={disabledLegalRequirement}
      shouldShowLegalRequirementLinkedInfo={shouldShowLegalRequirementLinkedInfo}
      selectedLegal={selectedLegal}
      setSelectedLegal={setSelectedLegal}
      getSelectedLrItem={(item) => {
        setSelectedLegal(item);
      }}
      aiSuggestedLegalIds={aiSuggestedLegalIds}
    />
  );
}

export default SelectLegalStandard;
